import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { errorText } from '../lib/apiErrors';
import { toast } from 'sonner';
import {
    Tag, Search, Trash2, Edit3, Loader2, Upload, MoreHorizontal,
    Merge, AlertTriangle, RefreshCw, BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_COLORS = [
    { name: 'Stone', value: '#78716c' },
    { name: 'Red', value: '#dc2626' },
    { name: 'Orange', value: '#ea580c' },
    { name: 'Amber', value: '#d97706' },
    { name: 'Green', value: '#16a34a' },
    { name: 'Emerald', value: '#059669' },
    { name: 'Teal', value: '#0d9488' },
    { name: 'Blue', value: '#2563eb' },
    { name: 'Indigo', value: '#4f46e5' },
    { name: 'Purple', value: '#9333ea' },
    { name: 'Pink', value: '#db2777' },
];

export default function TagManagementTab() {
    const { getAuthHeader } = useAuth();
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimerRef = useRef(null);
    const [selectedTags, setSelectedTags] = useState(new Set());

    // Delete state
    const [deleteTag, setDeleteTag] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Merge state
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeTarget, setMergeTarget] = useState('');
    const [merging, setMerging] = useState(false);

    // Import state
    const [showImportModal, setShowImportModal] = useState(false);
    const [csvText, setCsvText] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // Definitions (colors/categories)
    const [definitions, setDefinitions] = useState({});
    const [tagDialogOpen, setTagDialogOpen] = useState(false);
    const [tagDialogMode, setTagDialogMode] = useState('create'); // 'create' | 'edit'
    const [editingOriginalTag, setEditingOriginalTag] = useState(null);
    const [tagName, setTagName] = useState('');
    const [tagColor, setTagColor] = useState(TAG_COLORS[0].value);
    const [tagCategory, setTagCategory] = useState('');
    const [savingTag, setSavingTag] = useState(false);

    // Analytics
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('table'); // 'table' | 'analytics'

    const loadDefinitions = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/tags/definitions`, { headers: getAuthHeader() });
            const map = {};
            (res.data || []).forEach(d => { map[d.tag.toLowerCase()] = d; });
            setDefinitions(map);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const loadAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        try {
            const res = await axios.get(`${API}/tags/analytics`, { headers: getAuthHeader() });
            setAnalytics(res.data);
        } catch { toast.error('Failed to load analytics'); }
        finally { setAnalyticsLoading(false); }
    }, [getAuthHeader]);

    // Open the shared dialog for creating a new tag.
    const openCreateTag = () => {
        setTagDialogMode('create');
        setEditingOriginalTag(null);
        setTagName('');
        setTagColor(TAG_COLORS[0].value);
        setTagCategory('');
        setTagDialogOpen(true);
    };

    // Open the shared dialog for editing an existing tag (pre-filled).
    const openEditTag = (tag) => {
        const def = definitions[tag.tag.toLowerCase()] || {};
        setTagDialogMode('edit');
        setEditingOriginalTag(tag.tag);
        setTagName(tag.tag);
        setTagColor(def.color || TAG_COLORS[0].value);
        setTagCategory(def.category || '');
        setTagDialogOpen(true);
    };

    const handleSaveTag = async () => {
        const name = (tagName || '').trim();
        if (!name) { toast.error('Tag name is required'); return; }
        setSavingTag(true);
        try {
            if (tagDialogMode === 'edit') {
                if (name !== editingOriginalTag) {
                    await axios.put(`${API}/admin/tags/rename`, {
                        old_tag: editingOriginalTag,
                        new_tag: name,
                    }, { headers: getAuthHeader() });
                }
                const payload = { tag: name, color: tagColor };
                if (tagCategory) payload.category = tagCategory;
                await axios.post(`${API}/tags/definitions`, payload, { headers: getAuthHeader() });
                toast.success(`Updated "${name}"`);
            } else {
                const payload = { tag: name, color: tagColor };
                if (tagCategory) payload.category = tagCategory;
                await axios.post(`${API}/tags/definitions`, payload, { headers: getAuthHeader() });
                toast.success(`Created "${name}"`);
            }
            setTagDialogOpen(false);
            await Promise.all([loadTags(), loadDefinitions()]);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save tag');
        } finally {
            setSavingTag(false);
        }
    };

    // Debounce search input — wait 300ms after typing stops before firing API call
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [search]);

    const loadTags = useCallback(async () => {
        setLoading(true);
        try {
            const params = debouncedSearch ? `?search=${encodeURIComponent(debouncedSearch)}` : '';
            const res = await axios.get(`${API}/admin/tags${params}`, { headers: getAuthHeader() });
            setTags(res.data || []);
        } catch {
            toast.error('Failed to load tags');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, getAuthHeader]);

    useEffect(() => { loadTags(); loadDefinitions(); }, [loadTags, loadDefinitions]);

    // ── Delete ──
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await axios.delete(`${API}/admin/tags/${encodeURIComponent(deleteTag)}`, { headers: getAuthHeader() });
            toast.success(res.data.message);
            setDeleteTag(null);
            loadTags();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    // ── Merge ──
    const handleMerge = async () => {
        if (!mergeTarget.trim()) {
            toast.error('Enter a target tag name');
            return;
        }
        setMerging(true);
        try {
            const res = await axios.put(`${API}/admin/tags/merge`, {
                source_tags: Array.from(selectedTags),
                target_tag: mergeTarget.trim(),
            }, { headers: getAuthHeader() });
            toast.success(res.data.message);
            setShowMergeModal(false);
            setSelectedTags(new Set());
            setMergeTarget('');
            loadTags();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Merge failed');
        } finally {
            setMerging(false);
        }
    };

    // ── Import ──
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setCsvText(ev.target.result);
            setImportResult(null);
        };
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!csvText.trim()) {
            toast.error('Upload a CSV file first');
            return;
        }
        setImporting(true);
        try {
            const res = await axios.post(`${API}/admin/tags/import`, {
                csv_data: csvText,
            }, { headers: getAuthHeader() });
            setImportResult(res.data);
            toast.success(res.data.message);
            loadTags();
        } catch (err) {
            toast.error(errorText(err, 'Import failed'));
        } finally {
            setImporting(false);
        }
    };

    // ── Selection ──
    const toggleSelect = (tag) => {
        setSelectedTags(prev => {
            const next = new Set(prev);
            if (next.has(tag)) next.delete(tag);
            else next.add(tag);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedTags.size === tags.length) {
            setSelectedTags(new Set());
        } else {
            setSelectedTags(new Set(tags.map(t => t.tag)));
        }
    };

    const allSelected = tags.length > 0 && selectedTags.size === tags.length;
    const someSelected = selectedTags.size > 0 && selectedTags.size < tags.length;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tag Management</h2>
                    <p className="text-xs text-stone-500 mt-0.5">{tags.length} tags across contacts, companies, and deals</p>
                </div>
                <Tabs value={activeSection} onValueChange={(v) => { setActiveSection(v); if (v === 'analytics' && !analytics) loadAnalytics(); }}>
                    <TabsList>
                        <TabsTrigger value="table">Tags</TabsTrigger>
                        <TabsTrigger value="analytics">Analytics</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {activeSection === 'table' && (
            <>
            {/* Toolbar: search then actions */}
            <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                    <Input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search tags..."
                        className="pl-9"
                    />
                </div>
                {selectedTags.size >= 2 && (
                    <Button size="sm" variant="outline" onClick={() => { setMergeTarget(''); setShowMergeModal(true); }}>
                        <Merge className="w-4 h-4 mr-1" />Merge ({selectedTags.size})
                    </Button>
                )}
                <Button size="sm" onClick={openCreateTag}>
                    <Tag className="w-4 h-4 mr-1" />New Tag
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setCsvText(''); setImportResult(null); setShowImportModal(true); }}>
                    <Upload className="w-4 h-4 mr-1" />Import
                </Button>
                <Button size="sm" variant="outline" onClick={loadTags} title="Refresh">
                    <RefreshCw className="w-4 h-4" />
                </Button>
            </div>

            {/* Tag table */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                </div>
            ) : tags.length === 0 ? (
                <div className="text-center py-12">
                    <Tag className="w-10 h-10 text-stone-200 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">{search ? 'No tags match your search' : 'No tags found'}</p>
                </div>
            ) : (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <Table className="min-w-[600px]">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10 text-center"><Checkbox checked={allSelected ? true : (someSelected ? 'indeterminate' : false)} onCheckedChange={() => toggleSelectAll()} /></TableHead>
                                <TableHead className="w-16 text-center">Color</TableHead>
                                <TableHead>Tag</TableHead>
                                <TableHead className="w-20 text-center">Contacts</TableHead>
                                <TableHead className="w-20 text-center">Companies</TableHead>
                                <TableHead className="w-20 text-center">Deals</TableHead>
                                <TableHead className="w-16 text-center">Total</TableHead>
                                <TableHead className="w-24 text-right">&nbsp;</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tags.map(tag => (
                                <TableRow key={tag.tag} className={selectedTags.has(tag.tag) ? 'bg-muted/50' : ''}>
                                    <TableCell className="text-center"><Checkbox checked={selectedTags.has(tag.tag)} onCheckedChange={() => toggleSelect(tag.tag)} /></TableCell>
                                    <TableCell className="text-center"><span className="inline-block w-4 h-4 rounded-full border border-stone-200" style={{ backgroundColor: definitions[tag.tag.toLowerCase()]?.color || '#d6d3d1' }} /></TableCell>
                                    <TableCell className="font-medium">{tag.tag}</TableCell>
                                    <TableCell className="text-center text-muted-foreground">{tag.contacts || 0}</TableCell>
                                    <TableCell className="text-center text-muted-foreground">{tag.accounts || 0}</TableCell>
                                    <TableCell className="text-center text-muted-foreground">{tag.deals || 0}</TableCell>
                                    <TableCell className="text-center font-semibold">{tag.total || 0}</TableCell>
                                    <TableCell className="text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                    <MoreHorizontal className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openEditTag(tag)}>
                                                    <Edit3 className="w-4 h-4 mr-1" />
                                                    Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setDeleteTag(tag.tag)} className="text-red-600 focus:text-red-600">
                                                    <Trash2 className="w-4 h-4 mr-1" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
            </>
            )}

            {/* Analytics Section */}
            {activeSection === 'analytics' && (
                <div className="space-y-6">
                    {analyticsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                        </div>
                    ) : analytics ? (
                        <>
                            {/* Tag Frequency Distribution */}
                            {analytics.frequency?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Tag Frequency (Top 20)
                                    </h3>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={analytics.frequency} layout="vertical" margin={{ left: 100 }}>
                                            <XAxis type="number" tick={{ fontSize: 11 }} />
                                            <YAxis dataKey="tag" type="category" tick={{ fontSize: 11 }} width={90} />
                                            <Tooltip formatter={(val) => [val, 'Contacts']} />
                                            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                                {analytics.frequency.map((entry, i) => {
                                                    const defn = definitions[entry.tag?.toLowerCase()];
                                                    return <Cell key={i} fill={defn?.color || '#78716c'} />;
                                                })}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Tag Growth Over Time */}
                            {analytics.growth?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Tags Added Per Month
                                    </h3>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart data={analytics.growth}>
                                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <Tooltip formatter={(val) => [val, 'Tags added']} />
                                            <Bar dataKey="tags_added" fill="#78716c" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Conversion by Tag */}
                            {analytics.conversion?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Conversion Rate by Tag
                                    </h3>
                                    <div className="space-y-2">
                                        {analytics.conversion.map(c => (
                                            <div key={c.tag} className="flex items-center gap-3">
                                                <span className="text-xs text-stone-700 w-32 truncate font-medium"
                                                    style={definitions[c.tag?.toLowerCase()]?.color ? { borderLeft: `3px solid ${definitions[c.tag.toLowerCase()].color}`, paddingLeft: 8 } : {}}>
                                                    {c.tag}
                                                </span>
                                                <div className="flex-1 bg-stone-100 rounded-full h-4 overflow-hidden">
                                                    <div className="h-full rounded-full bg-emerald-500 transition-all"
                                                        style={{ width: `${Math.min(c.rate, 100)}%` }} />
                                                </div>
                                                <span className="text-xs text-stone-600 w-20 text-right">
                                                    {c.rate}% ({c.converted}/{c.total})
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tag Co-occurrences */}
                            {analytics.co_occurrences?.length > 0 && (
                                <div className="bg-white border border-stone-200 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Common Tag Pairs
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2">
                                        {analytics.co_occurrences.map(co => (
                                            <div key={co.pair} className="flex items-center justify-between px-3 py-2 bg-stone-50 rounded-lg">
                                                <span className="text-xs text-stone-700 font-medium">{co.pair}</span>
                                                <span className="text-[10px] text-stone-400">{co.count} contacts</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <BarChart3 className="w-10 h-10 text-stone-200 mx-auto mb-3" />
                            <p className="text-sm text-stone-500">No analytics data available</p>
                        </div>
                    )}
                </div>
            )}

            {/* Create / Edit Tag dialog */}
            <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{tagDialogMode === 'edit' ? 'Edit Tag' : 'New Tag'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-xs font-semibold text-stone-700 mb-1 block">Tag name</Label>
                            <Input
                                value={tagName}
                                onChange={e => setTagName(e.target.value)}
                                placeholder="e.g. VIP, Attended Webinar Q4"
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter' && tagName.trim()) handleSaveTag(); }}
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-700 mb-1 block">Colour</Label>
                            <div className="flex flex-wrap gap-1.5">
                                {TAG_COLORS.map(c => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        onClick={() => setTagColor(c.value)}
                                        title={c.name}
                                        className={`w-7 h-7 rounded-full border-2 transition-transform ${tagColor === c.value ? 'border-stone-900 scale-110' : 'border-transparent hover:scale-105'}`}
                                        style={{ backgroundColor: c.value }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-700 mb-1 block">Category <span className="text-stone-400 font-normal">(optional)</span></Label>
                            <Input
                                value={tagCategory}
                                onChange={e => setTagCategory(e.target.value)}
                                placeholder="e.g. Events, Segmentation, Stage"
                            />
                        </div>
                        <p className="text-[11px] text-stone-500">
                            {tagDialogMode === 'edit'
                                ? 'Updates the tag name, colour, and category. Renaming a tag carries its definition over to the new name.'
                                : 'Created tags appear in the grid immediately with 0 contacts and can be applied from the Contacts / Companies / Deals pages.'}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTagDialogOpen(false)} disabled={savingTag}>Cancel</Button>
                        <Button onClick={handleSaveTag} disabled={savingTag || !tagName.trim()}>
                            {savingTag ? 'Saving…' : tagDialogMode === 'edit' ? 'Save Changes' : 'Create Tag'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteTag} onOpenChange={() => setDeleteTag(null)}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" /> Delete Tag
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-stone-600 py-2">
                        Remove the tag <strong>"{deleteTag}"</strong> from all contacts, companies, and deals? This cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTag(null)}>Cancel</Button>
                        <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
                            {deleting ? 'Deleting...' : 'Delete Tag'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Merge Dialog */}
            <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Merge className="w-5 h-5" /> Merge Tags
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Tags to merge</Label>
                            <div className="flex flex-wrap gap-1.5 p-2 bg-stone-50 rounded-lg border border-stone-200">
                                {Array.from(selectedTags).map(t => (
                                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-stone-200 text-stone-700">
                                        <Tag className="w-3 h-3" /> {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Merge into (target tag)</Label>
                            <Input
                                value={mergeTarget}
                                onChange={e => setMergeTarget(e.target.value)}
                                placeholder="Enter the tag name to keep..."
                                autoFocus
                            />
                            <p className="text-[10px] text-stone-400 mt-1">All selected tags will be replaced with this tag</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowMergeModal(false)}>Cancel</Button>
                        <Button onClick={handleMerge} disabled={!mergeTarget.trim() || merging} className="bg-stone-900 hover:bg-stone-800">
                            {merging ? 'Merging...' : `Merge ${selectedTags.size} Tags`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Dialog */}
            <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Upload className="w-5 h-5" /> Import Tags from CSV
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
                            <p className="text-xs text-stone-600 font-medium mb-1">CSV Format</p>
                            <code className="text-[11px] text-stone-500 block">email,tags</code>
                            <code className="text-[11px] text-stone-500 block">john@acme.com,"VIP, Enterprise"</code>
                            <code className="text-[11px] text-stone-500 block">jane@corp.io,"Webinar, Decision Maker"</code>
                        </div>
                        <div>
                            <Label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Upload CSV</Label>
                            <Input type="file" accept=".csv,.txt" onChange={handleFileUpload}
                                className="file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200" />
                        </div>
                        {csvText && (
                            <div>
                                <p className="text-xs text-stone-500">{csvText.split('\n').length - 1} rows detected</p>
                            </div>
                        )}
                        {importResult && (
                            <div className={`rounded-lg p-3 ${importResult.matched > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                                <p className="text-sm font-medium text-stone-900">Import Results</p>
                                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                                    <div>Rows processed: <strong>{importResult.processed}</strong></div>
                                    <div>Contacts matched: <strong>{importResult.matched}</strong></div>
                                    <div>Not found: <strong>{importResult.not_found}</strong></div>
                                    <div>Tags applied: <strong>{importResult.tags_added}</strong></div>
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowImportModal(false)}>
                            {importResult ? 'Close' : 'Cancel'}
                        </Button>
                        {!importResult && (
                            <Button onClick={handleImport} disabled={!csvText || importing} className="bg-stone-900 hover:bg-stone-800">
                                {importing ? 'Importing...' : 'Import Tags'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
